import { useEffect, useRef, useState } from 'react';

import {
  listenToSyncReports,
  type SyncFailureReason,
  type SyncState,
} from './syncMessages';

export type AnswerSubmissionStatus =
  | 'idle'
  | 'saving'
  | 'saved'
  | 'saved-locally-offline'
  | 'saved-locally-syncing'
  | 'failed-to-sync';

const syncStateToSubmissionStatus: Record<SyncState, AnswerSubmissionStatus> = {
  failed: 'failed-to-sync',
  'queued-offline': 'saved-locally-offline',
  synced: 'saved',
  syncing: 'saved-locally-syncing',
};

// The requests a question on this page has been given. The shell's notice speaks for
// a refused answer only when no question here knows of it (RefusedAnswersNotice).
const followedRequestIds = new Set<string>();

export function isSyncReportFollowed(requestId: string) {
  return followedRequestIds.has(requestId);
}

export function useSyncStatus(requestId?: string | null) {
  // Kept by request: what an earlier request reported says nothing about this
  // one, which may only be queued. It listens from the start, as the page learns
  // a request's id from the worker's answer, and a report can come before that.
  const [reports, setReports] = useState<
    Record<
      string,
      { message: string | null; reason: SyncFailureReason | null; state: SyncState }
    >
  >({});

  useEffect(
    () =>
      listenToSyncReports((report) => {
        const reportedId = report.requestId;
        const reportedState = report.state;

        if (report.type !== 'sync-status' || !reportedId || !reportedState) {
          return;
        }

        setReports((current) => ({
          ...current,
          [reportedId]: {
            message: report.message ?? null,
            reason: report.reason ?? null,
            state: reportedState,
          },
        }));
      }),
    [],
  );

  // An earlier request stays followed after a newer one: the two are refused together,
  // and the question speaks for them with the newer one's report.
  const followedHereRef = useRef(new Set<string>());

  useEffect(() => {
    if (requestId) {
      followedHereRef.current.add(requestId);
      followedRequestIds.add(requestId);
    }
  }, [requestId]);

  useEffect(() => {
    const followedHere = followedHereRef.current;

    return () => {
      followedHere.forEach((id) => followedRequestIds.delete(id));
    };
  }, []);

  const currentReport = requestId ? reports[requestId] : undefined;

  return {
    message: currentReport?.message ?? null,
    reason: currentReport?.reason ?? null,
    status: currentReport ? syncStateToSubmissionStatus[currentReport.state] : 'idle',
    syncState: currentReport?.state ?? null,
  };
}

export function getAnswerSubmissionStatusLabel(status: AnswerSubmissionStatus) {
  switch (status) {
    case 'saving':
      return 'Saving';
    case 'saved':
      return 'Saved';
    case 'saved-locally-offline':
      return 'Saved locally · Offline';
    case 'saved-locally-syncing':
      return 'Saved locally · Syncing';
    case 'failed-to-sync':
      return 'Failed to sync';
    default:
      return null;
  }
}

